import React, { useEffect, useState } from "react";
import { MapPin, Thermometer } from "lucide-react";

const EMPTY_DRAFT = Object.freeze({ carpPresent: false, temperature: "" });
const FRESHWATER_WATERBODIES = new Set([
  "freshwater",
  "river",
  "stream",
  "lake_reservoir",
  "canal",
  "pond",
]);
const SALTWATER_WATERBODIES = new Set(["marine", "estuary_brackish"]);

export default function CommonCarpSessionInputs({
  context,
  isLoading,
  locationKey,
  onClear,
  onSubmit,
  submitted,
  t,
}) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDraft(EMPTY_DRAFT);
    setError(null);
  }, [locationKey]);

  const waterbody = context?.waterbody || "unknown";
  const temperature = context?.freshwaterTemperature || {};
  const profileNeedsWater =
    context?.locationPrecision === "profile_city" &&
    context?.waterbodyIdentified !== true;
  const trustedAutomatic =
    ["trusted_current", "trusted_timeline"].includes(temperature.status) &&
    temperature.source === "automatic" &&
    Number.isFinite(temperature.temperatureC);
  const actualFreshwater =
    context?.waterbodyIdentified === true && FRESHWATER_WATERBODIES.has(waterbody);

  if (profileNeedsWater) {
    return (
      <div className="rp-carp-session rp-carp-session--guidance" role="status">
        <MapPin size={21} aria-hidden="true" />
        <div>
          <h4>{t("fishing_today_carp_choose_water_title")}</h4>
          <p>{t("fishing_today_carp_choose_water_body")}</p>
        </div>
      </div>
    );
  }
  if (SALTWATER_WATERBODIES.has(waterbody)) return null;

  const submit = (event) => {
    event.preventDefault();
    const trimmed = draft.temperature.trim();
    const measuredTemperature = trimmed === "" ? null : Number(trimmed);
    if (measuredTemperature !== null && !Number.isFinite(measuredTemperature)) {
      setError("fishing_today_carp_temperature_invalid");
      return;
    }
    setError(null);
    onSubmit({
      carpPresent: draft.carpPresent,
      measuredFreshwaterTemperatureC: measuredTemperature,
    });
  };

  const clear = () => {
    setDraft(EMPTY_DRAFT);
    setError(null);
    onClear();
  };

  return (
    <form className="rp-carp-session" onSubmit={submit}>
      <div className="rp-carp-session__heading">
        <Thermometer size={19} aria-hidden="true" />
        <div>
          <h4>{t("fishing_today_carp_conditions_title")}</h4>
          {trustedAutomatic ? (
            <p className="rp-carp-session__automatic" role="status">
              {t("fishing_today_carp_automatic_temperature", {
                temperature: temperature.temperatureC,
              })}
            </p>
          ) : actualFreshwater ? (
            <p role="status">
              {temperature.status === "found_untrusted"
                ? t("fishing_today_carp_automatic_temperature_untrusted")
                : t("fishing_today_carp_automatic_temperature_unavailable")}
            </p>
          ) : (
            <p>{t("fishing_today_carp_waterbody_unconfirmed")}</p>
          )}
        </div>
      </div>
      {actualFreshwater ? (
        <label className="rp-carp-session__presence">
          <input
            checked={draft.carpPresent}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                carpPresent: event.target.checked,
              }))
            }
            type="checkbox"
          />
          <span>{t("fishing_today_carp_presence_label")}</span>
        </label>
      ) : null}
      {actualFreshwater && !trustedAutomatic ? (
        <details className="rp-carp-session__fallback">
          <summary>{t("fishing_today_carp_temperature_fallback_summary")}</summary>
          <label
            className="rp-carp-session__temperature"
            htmlFor="carp-measured-temperature"
          >
            <span>{t("fishing_today_carp_temperature_label")}</span>
            <span className="rp-carp-session__temperature-control">
              <input
                aria-describedby="carp-temperature-helper"
                aria-label={t("fishing_today_carp_temperature_label")}
                id="carp-measured-temperature"
                inputMode="decimal"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    temperature: event.target.value,
                  }))
                }
                type="text"
                value={draft.temperature}
              />
              <span aria-hidden="true">°C</span>
            </span>
            <small id="carp-temperature-helper">
              {t("fishing_today_carp_temperature_helper")}
            </small>
          </label>
        </details>
      ) : null}
      {error ? (
        <p className="rp-carp-session__error" role="alert">{t(error)}</p>
      ) : null}
      {actualFreshwater ? (
        <div className="rp-carp-session__actions">
          <button disabled={isLoading} type="submit">
            {t("fishing_today_carp_update_conditions")}
          </button>
          {submitted ? (
            <button disabled={isLoading} onClick={clear} type="button">
              {t("fishing_today_carp_clear_conditions")}
            </button>
          ) : null}
        </div>
      ) : null}
      {submitted ? (
        <p className="rp-carp-session__source" role="status">
          {t("fishing_today_carp_current_source_note")}
        </p>
      ) : null}
    </form>
  );
}
